import * as RadixTabs from '@radix-ui/react-tabs';
import { forwardRef, useState } from 'react';
import { Popover } from '~/components/granular/popover';
import { Separator } from '~/components/granular/separator';
import { useFormatMessage } from '~/intl';
import { classNames } from '~/utils/styles';
import { TabsSkeleton, Tooltip } from '..';
import type { TabContentProps, TabProps, TabsProps } from './Tabs.types';
import { useTabGroup } from './useTabGroup';

export function Tabs({ ...props }: TabsProps) {
  const formatMessage = useFormatMessage();

  const {
    children,
    className,
    classNameList,
    defaultValue,
    isLoading,
    listAriaLabel,
    moreTabsLabel = formatMessage({ id: 'common.more' }),
    rightMenu,
    staticTabs = [],
    tabDescription,
    tabs = [],
    value,
    onValueChange,
  } = props;

  const [selectedTab, setSelectedTab] = useState<string | undefined>(defaultValue);
  const { tabListRef, visibleTabs, handleSelectedTabChange } = useTabGroup({ selectedTab, tabs });

  function handleTabChange(value: string) {
    setSelectedTab(value);
    handleSelectedTabChange(value);

    if (onValueChange) {
      onValueChange(value);
    }
  }

  if (isLoading) {
    return <TabsSkeleton count={tabs.length} staticItemsCount={staticTabs.length} />;
  }

  return (
    <RadixTabs.Root className={className} defaultValue={defaultValue} onValueChange={handleTabChange} value={value}>
      <RadixTabs.List
        aria-label={listAriaLabel}
        className={classNames('flex shrink-0 grow gap-2 border-b border-neutral-300', classNameList)}
        ref={tabListRef}
      >
        {tabDescription && <div className="label-300 py-[10px] text-neutral-1000">{tabDescription}</div>}

        <TabList tabs={visibleTabs.filter((tab) => !tab.hidden)} />

        {!!visibleTabs.filter((tab) => tab.hidden).length && (
          <GroupedTabButton dropdownTabs={visibleTabs.filter((tab) => tab.hidden)} label={moreTabsLabel} />
        )}

        {!!staticTabs.length && (
          <>
            <div className="py-2" key={`${value}-separator`}>
              <Separator orientation="vertical" />
            </div>
            <TabList tabs={staticTabs} />
          </>
        )}

        {rightMenu && <div className="ml-auto flex items-center">{rightMenu}</div>}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  );
}

const tabClassListDefault = 'label-200 text-nowrap px-1 py-[10px] items-center justify-center outline-hidden';
const tabClassListEnabled =
  'text-neutral-800 hover:text-neutral-1000 data-[state=active]:text-neutral-1000 data-[state=active]:shadow-[inset_0_-1px_0_0,0_1px_0_0] data-[state=active]:shadow-teal-700';

export const TabButton = forwardRef<HTMLButtonElement, TabProps>(
  ({ label, value, disabled, tooltip }, forwardedRef) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    function handleTooltipOpenChange(open: boolean) {
      if (!tooltip) {
        return;
      }

      setIsTooltipOpen(open);
    }

    const tabClassList = classNames(tabClassListEnabled, {
      'opacity-50': disabled,
    });

    return (
      <RadixTabs.Trigger className={tabClassList} disabled={disabled} ref={forwardedRef} value={value}>
        <Tooltip
          disabled={!tooltip}
          onOpenChange={handleTooltipOpenChange}
          open={isTooltipOpen}
          trigger={<div className={classNames(tabClassListDefault, 'flex')}>{label}</div>}
        >
          {tooltip}
        </Tooltip>
      </RadixTabs.Trigger>
    );
  },
);

export function GroupedTabButton({ dropdownTabs, label }: { dropdownTabs: Array<TabProps>; label: string }) {
  return (
    <Popover
      side="bottom"
      trigger={
        <button className={classNames(tabClassListDefault, tabClassListEnabled)} type="button">
          {label}
        </button>
      }
    >
      <DropdownTabList tabs={dropdownTabs} />
    </Popover>
  );
}

export function TabList({ tabs }: { tabs: Array<TabProps> }) {
  if (!tabs.length) {
    return null;
  }
  return tabs.map(({ value, ...tabProps }) => <TabButton key={value} value={value} {...tabProps} />);
}

export function DropdownTabList({ tabs }: { tabs: Array<TabProps> }) {
  if (!tabs.length) {
    return null;
  }

  return tabs.map(({ label, value }) => (
    <div className="flex flex-col px-4 py-3" key={value}>
      <RadixTabs.Trigger
        className="body-200-light px-2 py-2 text-left hover:bg-neutral-200 data-[state=active]:bg-teal-200"
        value={value}
      >
        {label}
      </RadixTabs.Trigger>
    </div>
  ));
}

export function TabContent({ children, value, className }: TabContentProps) {
  return (
    <RadixTabs.Content className={className} value={value}>
      {children}
    </RadixTabs.Content>
  );
}
